import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import DashboardLayout from '../components/DashboardLayout';
import StrategyWebSocketClient from '../services/StrategyWebSocketClient';
import { sessionService, psmSessionService, agentService, getAgentWebSocketUrl } from '../config/api';
import ThoughtBlock from '../components/ThoughtBlock';
import ChatInput from '../components/ChatInput';
import ConfirmDialog from '../components/ConfirmDialog';
import './StrategyChatRedesign.css';

/**
 * Strategy Chat page for AI-assisted strategy creation.
 * Features stacked User/System message cards with glassmorphism design.
 */
const StrategyChat = () => {
    const [messages, setMessages] = useState([]);
    const [inputText, setInputText] = useState('');
    const [isConnected, setIsConnected] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sessionId, setSessionId] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [selectedSession, setSelectedSession] = useState(null);
    const [agentState, setAgentState] = useState(null);
    const [subAgent, setSubAgent] = useState(null);
    const [isLoadingSessions, setIsLoadingSessions] = useState(true);
    const [collapsedCards, setCollapsedCards] = useState({});
    const [availableAgents, setAvailableAgents] = useState([]);
    const [selectedAgentName, setSelectedAgentName] = useState('strategy-orchestrator');
    const [pendingPermission, setPendingPermission] = useState(null); // HITL permission request
    const [isRestoring, setIsRestoring] = useState(false); // Session restore in progress
    const [dialogConfig, setDialogConfig] = useState({ isOpen: false, title: '', message: '', onConfirm: null });

    const conversationEndRef = useRef(null);
    const wsClient = useRef(null);

    /**
     * Group messages into conversation blocks:
     * Each block is either a user message or a group of system messages
     */
    const groupMessages = () => {
        const blocks = [];
        let currentSystemBlock = null;

        messages.forEach((msg, index) => {
            if (msg.role === 'user') {
                // If there's a pending system block, push it first
                if (currentSystemBlock) {
                    blocks.push(currentSystemBlock);
                    currentSystemBlock = null;
                }
                // Add user message as its own block
                blocks.push({
                    type: 'user',
                    id: `user-${index}`,
                    message: msg
                });
            } else if (msg.role === 'assistant' && msg.finished) {
                // Final message - separate block
                if (currentSystemBlock) {
                    blocks.push(currentSystemBlock);
                    currentSystemBlock = null;
                }
                blocks.push({
                    type: 'final',
                    id: `final-${index}`,
                    message: msg
                });
            } else {
                // System/thinking/working messages - group them
                if (!currentSystemBlock) {
                    currentSystemBlock = {
                        type: 'system',
                        id: `system-${index}`,
                        messages: []
                    };
                }
                currentSystemBlock.messages.push({ ...msg, index });
            }
        });

        // Don't forget the last system block
        if (currentSystemBlock) {
            blocks.push(currentSystemBlock);
        }

        return blocks;
    };

    const conversationBlocks = groupMessages();

    // Fetch sessions from PSM API on mount (includes ACTIVE + ARCHIVED)
    const fetchSessions = async () => {
        try {
            setIsLoadingSessions(true);
            const result = await psmSessionService.listSessions();
            const sessionList = result.sessions || [];
            setSessions(sessionList);
        } catch (err) {
            console.error('[StrategyChat] Error fetching PSM sessions:', err);
            setSessions([]);
        } finally {
            setIsLoadingSessions(false);
        }
    };

    useEffect(() => {
        fetchSessions();
    }, []);

    // Fetch available agents on mount
    useEffect(() => {
        const fetchAgents = async () => {
            try {
                const agents = await agentService.getAvailableAgents();
                // Filter to only agents with WebSocket support
                const wsAgents = agents.filter(a =>
                    ['strategy-orchestrator', 'code-explorer', 'debugger', 'general-assistant'].includes(a.name)
                );
                setAvailableAgents(wsAgents);
            } catch (err) {
                console.error('[StrategyChat] Error fetching agents:', err);
                // Fallback to default agents
                setAvailableAgents([
                    { name: 'strategy-orchestrator', displayName: 'Strategy Orchestrator' },
                    { name: 'code-explorer', displayName: 'Code Explorer' },
                    { name: 'debugger', displayName: 'Debugger Agent' },
                    { name: 'general-assistant', displayName: 'General Assistant' }
                ]);
            }
        };
        fetchAgents();
    }, []);

    // Connect to WebSocket
    const connectToAgent = (existingSessionId = null) => {
        const token = localStorage.getItem('token');
        if (!token) {
            setError('No authentication token found. Please login.');
            return;
        }

        if (wsClient.current) {
            wsClient.current.close();
        }

        // Get WebSocket URL for selected agent
        const wsUrl = getAgentWebSocketUrl(selectedAgentName);
        wsClient.current = new StrategyWebSocketClient(wsUrl);

        const handleMessage = (message) => {
            switch (message.type) {
                case 'PARTIAL':
                    setMessages(prev => {
                        const last = prev[prev.length - 1];
                        if (last && last.role === 'assistant' && !last.finished) {
                            return [...prev.slice(0, -1), {
                                ...last,
                                content: last.content + message.content,
                                finished: message.finished || false,
                                waitingForUser: message.waitingForUser || false,
                                subAgent: message.subAgent || last.subAgent
                            }];
                        } else {
                            return [...prev, {
                                role: 'assistant',
                                content: message.content,
                                finished: message.finished || false,
                                waitingForUser: message.waitingForUser || false,
                                subAgent: message.subAgent
                            }];
                        }
                    });
                    if (message.finished || message.waitingForUser) {
                        setIsLoading(false);
                        setAgentState(null);
                        setSubAgent(null);
                    }
                    break;

                case 'THINKING':
                case 'WORKING':
                    setMessages(prev => [...prev, {
                        role: 'system',
                        content: message.content,
                        type: message.type.toLowerCase(),
                        subAgent: message.subAgent
                    }]);
                    break;

                case 'ERROR':
                    setError(message.content);
                    setIsLoading(false);
                    setAgentState(null);
                    break;

                case 'PERMISSION_REQUIRED':
                    // HITL: Agent is asking for permission to run a CLI command
                    setPendingPermission({
                        commandId: message.commandId,
                        command: message.command,
                        message: message.message
                    });
                    setIsLoading(false);
                    break;

                case 'REPLAY_USER_MESSAGE':
                    // Replayed user message from reconnect — add to chat history
                    setMessages(prev => [...prev, {
                        role: 'user',
                        content: message.content
                    }]);
                    break;

                default:
                    break;
            }
        };

        const handleStateChange = (state, agent) => {
            setAgentState(state);
            setSubAgent(agent);
        };

        wsClient.current.onMessage(handleMessage);
        wsClient.current.onStateChange(handleStateChange);

        wsClient.current.connect(token, existingSessionId, selectedAgentName)
            .then(newSessionId => {
                setSessionId(newSessionId);
                setIsConnected(true);

                if (!existingSessionId) {
                    const newSession = {
                        id: newSessionId,
                        agentName: selectedAgentName,
                        status: 'ACTIVE',
                        createdAt: new Date().toISOString(),
                        lastUpdateTime: new Date().toISOString()
                    };
                    setSessions(prev => [newSession, ...prev]);
                    setSelectedSession(newSession);
                    // Refresh full session list to show archived sessions
                    fetchSessions();

                    setMessages([{
                        role: 'system',
                        content: 'Connected to Strategy AI. How can I help you create a trading strategy?',
                        type: 'system'
                    }]);
                }
                // When resuming an existing session, don't overwrite messages
                // (history was already loaded in handleSelectSession)
            })
            .catch(err => {
                setError(`Connection failed: ${err.message}`);
                setIsConnected(false);
            });
    };

    useEffect(() => {
        return () => {
            if (wsClient.current) {
                wsClient.current.close();
            }
        };
    }, []);

    useEffect(() => {
        conversationEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSendMessage = (e) => {
        e.preventDefault();
        if (!inputText.trim() || !isConnected || isLoading) return;

        const userMessage = { role: 'user', content: inputText };
        setMessages(prev => [...prev, userMessage]);

        if (selectedSession) {
            setSessions(prev => prev.map(s =>
                s.id === selectedSession.id
                    ? { ...s, lastMessage: inputText.substring(0, 50), title: inputText.substring(0, 30) + '...' }
                    : s
            ));
        }

        wsClient.current.sendMessage(inputText);
        setInputText('');
        setIsLoading(true);
    };

    const handleDisconnect = () => {
        if (wsClient.current) wsClient.current.close();
        setIsConnected(false);
        setSessionId(null);
        setAgentState(null);
        setSubAgent(null);
        setMessages(prev => [...prev, { role: 'system', content: 'Disconnected from agent', type: 'system' }]);
    };

    const handleNewChat = () => {
        if (isConnected) handleDisconnect();
        setMessages([]);
        setSelectedSession(null);
        setCollapsedCards({});
        connectToAgent();
        // Refresh sessions to show the newly archived session
        fetchSessions();
    };

    const handleSelectSession = async (session) => {
        // ARCHIVING sessions cannot be selected — they are in transition
        if (session.status === 'ARCHIVING') {
            return;
        }

        // If session is ARCHIVED, restore it from S3 first
        if (session.status === 'ARCHIVED') {
            await handleRestoreSession(session);
            return;
        }

        // Expired sessions still have events in MongoDB - just reconnect
        setSelectedSession(session);
        setCollapsedCards({});

        // Load conversation history from backend before connecting
        try {
            const result = await sessionService.getSessionMessages(session.id);
            const historyMessages = (result.messages || []).map(msg => ({
                role: msg.role,
                content: msg.content,
                type: msg.type || undefined,
                finished: msg.role === 'assistant' ? true : undefined,
                subAgent: msg.subAgent || undefined
            }));
            setMessages(historyMessages.length > 0
                ? historyMessages
                : [{ role: 'system', content: 'Resuming session...', type: 'system' }]
            );
        } catch (err) {
            console.warn('[StrategyChat] Could not load session history:', err);
            setMessages([{ role: 'system', content: 'Resuming session...', type: 'system' }]);
        }

        connectToAgent(session.id);
    };

    const handleRestoreSession = async (session) => {
        if (isRestoring) return;

        setIsRestoring(true);
        setError(null);

        try {
            const result = await psmSessionService.restoreSession(session.id);
            const restoredId = result.restoredSessionId;

            // Refresh session list to reflect new status
            await fetchSessions();

            // Find the restored session in updated list
            const restoredSession = { ...session, id: restoredId, status: 'ACTIVE' };
            setSelectedSession(restoredSession);
            setCollapsedCards({});

            // Load conversation history from the restored session
            try {
                const historyResult = await sessionService.getSessionMessages(restoredId);
                const historyMessages = (historyResult.messages || []).map(msg => ({
                    role: msg.role,
                    content: msg.content,
                    type: msg.type || undefined,
                    finished: msg.role === 'assistant' ? true : undefined,
                    subAgent: msg.subAgent || undefined
                }));
                setMessages(historyMessages.length > 0
                    ? [...historyMessages, {
                        role: 'system',
                        content: `Session restored from archive (${result.eventCount || 0} events). You can continue your conversation.`,
                        type: 'system'
                    }]
                    : [{ role: 'system', content: `Session restored from archive (${result.eventCount || 0} events). You can continue your conversation.`, type: 'system' }]
                );
            } catch (histErr) {
                console.warn('[StrategyChat] Could not load restored session history:', histErr);
                setMessages([{ role: 'system', content: `Session restored from archive (${result.eventCount || 0} events). You can continue your conversation.`, type: 'system' }]);
            }

            // Connect via WebSocket to the restored session
            connectToAgent(restoredId);
        } catch (err) {
            console.error('[StrategyChat] Error restoring session:', err);
            setError(`Failed to restore session: ${err.message}`);
        } finally {
            setIsRestoring(false);
        }
    };

    const handleArchiveSession = async (sessionIdToArchive, e) => {
        e.stopPropagation();
        setDialogConfig({
            isOpen: true,
            title: 'Archive Session',
            message: 'Archive this session? It will be saved to S3 and can be restored later.',
            onConfirm: async () => {
                setDialogConfig({ isOpen: false });
                try {
                    await psmSessionService.archiveSession(sessionIdToArchive);
                    // Refresh sessions to reflect new status
                    await fetchSessions();
                    if (selectedSession?.id === sessionIdToArchive) {
                        handleDisconnect();
                        setSelectedSession(null);
                    }
                } catch (err) {
                    setError(`Failed to archive session: ${err.message}`);
                }
            }
        });
    };

    const handleDeleteSession = async (sessionIdToDelete, e) => {
        e.stopPropagation();
        setDialogConfig({
            isOpen: true,
            title: 'Delete Session',
            message: 'Are you sure you want to delete this chat session?',
            onConfirm: async () => {
                setDialogConfig({ isOpen: false });
                try {
                    // Under the new PSM architecture, 'delete' means we archive it to S3
                    await psmSessionService.archiveSession(sessionIdToDelete);
                    await fetchSessions(); // Refresh sessions list
                    if (selectedSession?.id === sessionIdToDelete) handleNewChat();
                } catch (err) {
                    setError('Failed to delete (archive) session: ' + err.message);
                }
            }
        });
    };

    const closeDialog = () => setDialogConfig({ isOpen: false });

    const toggleCardCollapse = (cardId) => {
        setCollapsedCards(prev => ({
            ...prev,
            [cardId]: !prev[cardId]
        }));
    };

    // HITL permission handlers
    const handleGrantPermission = () => {
        if (pendingPermission && wsClient.current) {
            wsClient.current.grantPermission(pendingPermission.commandId);
            setMessages(prev => [...prev, {
                role: 'system',
                content: `✅ Permission granted for: ${pendingPermission.command}`,
                type: 'permission'
            }]);
            setPendingPermission(null);
            setIsLoading(true);
        }
    };

    const handleDenyPermission = () => {
        if (pendingPermission && wsClient.current) {
            wsClient.current.denyPermission(pendingPermission.commandId);
            setMessages(prev => [...prev, {
                role: 'system',
                content: `❌ Permission denied for: ${pendingPermission.command}`,
                type: 'permission'
            }]);
            setPendingPermission(null);
        }
    };

    const getSubAgentInfo = (agent) => {
        switch (agent) {
            // Strategy Orchestrator sub-agents
            case 'planner': return { icon: '📋', label: 'Planner', className: 'planner' };
            case 'builder': return { icon: '🔧', label: 'Builder', className: 'builder' };
            case 'validator': return { icon: '✅', label: 'Validator', className: 'validator' };
            // Code Explorer tools
            case 'cloneRepository': return { icon: '📥', label: 'Clone Repo', className: 'tool' };
            case 'searchCode': return { icon: '🔍', label: 'Search Code', className: 'tool' };
            case 'readFile': return { icon: '📄', label: 'Read File', className: 'tool' };
            case 'executeCommand': return { icon: '⚡', label: 'CLI Command', className: 'tool' };
            case 'searchWeb': return { icon: '🌐', label: 'Web Search', className: 'tool' };
            case 'getFileTree': return { icon: '🌲', label: 'File Tree', className: 'tool' };
            case 'listFiles': return { icon: '📂', label: 'List Files', className: 'tool' };
            case 'getGitHistory': return { icon: '📜', label: 'Git History', className: 'tool' };
            case 'analyzeCode': return { icon: '🔬', label: 'Analyze Code', className: 'tool' };
            default: return null;
        }
    };

    const getMessageTypeInfo = (type, subAgent) => {
        if (subAgent) {
            const info = getSubAgentInfo(subAgent);
            if (info) return info;
        }
        switch (type) {
            case 'thinking': return { icon: '💭', label: 'Thinking', className: 'thinking' };
            case 'working': return { icon: '⚙️', label: 'Working', className: 'working' };
            default: return { icon: 'ℹ️', label: 'System', className: 'system' };
        }
    };

    // Render User Message Card
    const renderUserCard = (block) => (
        <div key={block.id} className="chat chat-end animate-fade-in">
            <div className="chat-image avatar">
                <div className="w-10 rounded-full bg-primary text-primary-content grid place-items-center">
                    <span className="text-xs font-bold">YOU</span>
                </div>
            </div>
            <div className="chat-header text-xs opacity-50 mb-1">
                You
            </div>
            <div className="chat-bubble chat-bubble-primary shadow-sm text-sm">
                <ReactMarkdown>{block.message.content}</ReactMarkdown>
            </div>
        </div>
    );

    // Render System Message Card (Collapsible Thought Block)
    const renderSystemCard = (block) => {
        const isCollapsed = collapsedCards[block.id] !== false; // Default to collapsed
        // If it's the last block and loading, default to open
        const isLastAndLoading = isLoading && block.id === conversationBlocks[conversationBlocks.length - 1]?.id;

        // Determine status based on last message in block
        const lastMsg = block.messages[block.messages.length - 1];
        const status = lastMsg?.type?.toUpperCase() || 'THINKING';

        return (
            <div key={block.id} className="chat chat-start animate-fade-in w-full">
                <div className="chat-image avatar">
                    <div className="w-10 rounded-full bg-base-300 grid place-items-center">
                        <span className="text-xl">🤖</span>
                    </div>
                </div>
                <div className="chat-header text-xs opacity-50 mb-1">
                    Strategy AI
                </div>
                <div className="chat-bubble bg-transparent shadow-none p-0 w-full max-w-4xl">
                    <ThoughtBlock
                        messages={block.messages}
                        isOpen={!isCollapsed || isLastAndLoading}
                        onToggle={() => toggleCardCollapse(block.id)}
                        status={status === 'WORKING' ? 'Working on it...' : 'Thinking Process'}
                        isLive={isLastAndLoading}
                    />
                </div>
            </div>
        );
    };

    // Render Final Message Card
    const renderFinalCard = (block) => {
        const subAgentInfo = getSubAgentInfo(block.message.subAgent);
        return (
            <div key={block.id} className="chat chat-start animate-fade-in">
                <div className="chat-image avatar">
                    <div className="w-10 rounded-full bg-base-300 grid place-items-center">
                        <span className="text-xl">🤖</span>
                    </div>
                </div>
                <div className="chat-header text-xs opacity-50 mb-1">
                    Strategy AI
                </div>
                <div className="chat-bubble bg-base-100 text-base-content border border-base-200 shadow-sm w-full max-w-4xl p-6">
                    {block.message.waitingForUser && (
                        <div className="badge badge-warning gap-2 mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="inline-block w-4 h-4 stroke-current"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                            Waiting for input
                        </div>
                    )}

                    <div className="markdown-content">
                        <ReactMarkdown>{block.message.content}</ReactMarkdown>
                    </div>

                    {subAgentInfo && (
                        <div className="flex items-center gap-2 mt-4 pt-4 border-t border-base-200 text-xs opacity-60">
                            {subAgentInfo.icon} Processed by <span className="font-semibold">{subAgentInfo.label}</span>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <DashboardLayout>
            <div className="strategy-chat">
                <div className="strategy-chat-container">
                    {/* Main Chat Area (Left) */}
                    <div className="strategy-chat-main glass-container">
                        {/* Chat Header */}
                        <div className="strategy-chat-header">
                            <div className="header-left">
                                <h2>🤖 AI Agent</h2>
                                {/* Agent Selector */}
                                <select
                                    className="agent-selector"
                                    value={selectedAgentName}
                                    onChange={(e) => setSelectedAgentName(e.target.value)}
                                    disabled={isConnected}
                                >
                                    {availableAgents.map(agent => (
                                        <option key={agent.name} value={agent.name}>
                                            {agent.displayName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="header-controls">
                                {isConnected ? (
                                    <button className="btn btn-sm btn-error" onClick={handleDisconnect}>
                                        Disconnect
                                    </button>
                                ) : (
                                    <button className="btn btn-sm btn-success" onClick={() => connectToAgent()}>
                                        Connect
                                    </button>
                                )}
                                <div className={`status-badge ${isConnected ? 'connected' : 'disconnected'}`}>
                                    <div className="pulse-dot"></div>
                                    {isConnected ? 'Connected' : 'Disconnected'}
                                </div>
                                {sessionId && (
                                    <div className="badge badge-ghost font-mono text-xs">
                                        {sessionId.substring(0, 8)}...
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Agent State Banner */}
                        {agentState && (
                            <div className="agent-state-banner">
                                <span className={`state-badge ${agentState.toLowerCase()}`}>
                                    {agentState === 'THINKING' && '💭'}
                                    {agentState === 'WORKING' && '⚙️'}
                                    {agentState}
                                </span>
                                {subAgent && (
                                    <span className="subagent-label">
                                        {getSubAgentInfo(subAgent)?.icon} {getSubAgentInfo(subAgent)?.label}
                                    </span>
                                )}
                            </div>
                        )}

                        {/* Error Alert */}
                        {error && (
                            <div className="strategy-error-alert">
                                <span className="icon">⚠️</span>
                                <span className="message">{error}</span>
                                <button onClick={() => setError(null)}>✕</button>
                            </div>
                        )}

                        {/* HITL Permission Request Dialog */}
                        {pendingPermission && (
                            <div className="permission-dialog">
                                <div className="permission-content">
                                    <div className="permission-header">
                                        <span className="icon">⚡</span>
                                        <h4>Permission Required</h4>
                                    </div>
                                    <p className="permission-message">{pendingPermission.message}</p>
                                    <code className="permission-command">{pendingPermission.command}</code>
                                    <div className="permission-actions">
                                        <button
                                            className="btn btn-success btn-sm"
                                            onClick={handleGrantPermission}
                                        >
                                            ✅ Allow
                                        </button>
                                        <button
                                            className="btn btn-error btn-sm"
                                            onClick={handleDenyPermission}
                                        >
                                            ❌ Deny
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Restoring Session Overlay */}
                        {isRestoring && (
                            <div className="flex items-center gap-3 p-3 bg-info/10 border border-info/20 rounded-lg mx-4 mt-2">
                                <span className="loading loading-spinner loading-sm text-info"></span>
                                <span className="text-sm text-info">Restoring session from archive...</span>
                            </div>
                        )}

                        {/* Chat Content - Stacked Cards */}
                        <div className="strategy-chat-content bg-base-100 flex-1 flex flex-col min-h-0 overflow-hidden relative">
                            <div className="flex-1 overflow-y-auto p-4 space-y-6 scroll-smooth">
                                {conversationBlocks.length === 0 ? (
                                    <div className="h-full flex flex-col items-center justify-center text-center p-8 opacity-60">
                                        <div className="text-6xl mb-4">🤖</div>
                                        <h3 className="text-xl font-bold mb-2">Strategy AI Assistant</h3>
                                        <p className="max-w-md">
                                            {isConnected
                                                ? 'Start a conversation to create your trading strategy. I can help you plan, build, and validate algorithmic strategies.'
                                                : 'Connect to the AI to begin creating strategies'}
                                        </p>
                                    </div>
                                ) : (
                                    conversationBlocks.map(block => {
                                        switch (block.type) {
                                            case 'user':
                                                return renderUserCard(block);
                                            case 'system':
                                                return renderSystemCard(block);
                                            case 'final':
                                                return renderFinalCard(block);
                                            default:
                                                return null;
                                        }
                                    })
                                )}

                                {/* Show typing indicator/Thinking block if loading and not already shown */}
                                {isLoading && conversationBlocks.length > 0 &&
                                    conversationBlocks[conversationBlocks.length - 1].type !== 'system' && (
                                        <div className="animate-fade-in">
                                            <div className="flex items-center gap-2 p-4 text-sm text-base-content/50">
                                                <span className="loading loading-dots loading-sm"></span>
                                                <span>Thinking...</span>
                                            </div>
                                        </div>
                                    )}

                                <div ref={conversationEndRef} />
                            </div>
                        </div>

                        {/* Input Area */}
                        <div className="p-4 border-t border-base-200 bg-base-100">
                            <ChatInput
                                value={inputText}
                                onChange={setInputText}
                                onSend={(e) => {
                                    if (e) e.preventDefault();
                                    handleSendMessage(e);
                                }}
                                isConnected={isConnected}
                                isLoading={isLoading}
                                selectedAgent={selectedAgentName}
                                availableAgents={availableAgents}
                                onSelectAgent={setSelectedAgentName}
                            />
                        </div>
                    </div>

                    {/* Sessions Sidebar (Right) */}
                    <div className="strategy-sessions-sidebar glass-container">
                        <div className="strategy-sessions-header">
                            <h3>Sessions</h3>
                            <button
                                className="btn btn-primary btn-sm btn-circle"
                                onClick={handleNewChat}
                                title="New Strategy"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>

                        <div className="strategy-sessions-list">
                            {isLoadingSessions ? (
                                <div className="flex justify-center p-4">
                                    <span className="loading loading-spinner loading-sm"></span>
                                </div>
                            ) : sessions.length === 0 ? (
                                <div className="text-center text-base-content/50 text-sm py-8">
                                    No strategy sessions yet
                                </div>
                            ) : (
                                sessions.map(session => {
                                    const isArchived = session.status === 'ARCHIVED';
                                    const isArchiving = session.status === 'ARCHIVING';
                                    const isExpired = session.status === 'EXPIRED';
                                    const isInactive = isArchived || isArchiving || isExpired;
                                    const sessionTitle = session.title || session.agentName || 'Chat Session';
                                    const sessionDate = session.lastUpdateTime || session.createdAt;

                                    return (
                                        <div
                                            key={session.id}
                                            className={`strategy-session-card ${selectedSession?.id === session.id ? 'active' : ''} ${isInactive ? 'opacity-70' : ''}`}
                                            onClick={() => handleSelectSession(session)}
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <h4 className="truncate">{sessionTitle}</h4>
                                                        {isArchived && (
                                                            <span className="badge badge-xs badge-ghost">Archived</span>
                                                        )}
                                                        {isArchiving && (
                                                            <span className="badge badge-xs badge-warning">Archiving</span>
                                                        )}
                                                        {isExpired && (
                                                            <span className="badge badge-xs badge-ghost">Expired</span>
                                                        )}
                                                    </div>
                                                    {session.eventCount && (
                                                        <p className="text-xs opacity-50">{session.eventCount} messages</p>
                                                    )}
                                                    <p className="text-xs opacity-40 mt-1">
                                                        {sessionDate ? new Date(sessionDate).toLocaleDateString() : ''}
                                                    </p>
                                                </div>
                                                <div className="flex flex-col gap-1">
                                                    {isArchiving ? (
                                                        <span className="loading loading-spinner loading-xs opacity-50" title="Archiving in progress"></span>
                                                    ) : isArchived ? (
                                                        <button
                                                            className="btn btn-ghost btn-xs btn-circle opacity-50 hover:opacity-100"
                                                            onClick={(e) => { e.stopPropagation(); handleRestoreSession(session); }}
                                                            title="Restore session"
                                                            disabled={isRestoring}
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                                                            </svg>
                                                        </button>
                                                    ) : (
                                                        <>
                                                            <button
                                                                className="btn btn-ghost btn-xs btn-circle opacity-50 hover:opacity-100"
                                                                onClick={(e) => handleArchiveSession(session.id, e)}
                                                                title="Archive session"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                                    <path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" />
                                                                    <path fillRule="evenodd" d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" clipRule="evenodd" />
                                                                </svg>
                                                            </button>
                                                            <button
                                                                className="btn btn-ghost btn-xs btn-circle opacity-50 hover:opacity-100"
                                                                onClick={(e) => handleDeleteSession(session.id, e)}
                                                                title="Delete session"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                                                </svg>
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <ConfirmDialog
                isOpen={dialogConfig.isOpen}
                title={dialogConfig.title}
                message={dialogConfig.message}
                onConfirm={dialogConfig.onConfirm}
                onCancel={closeDialog}
            />
        </DashboardLayout>
    );
};

export default StrategyChat;
